/*
 * Date-only helpers. Everything works on local-midnight Date objects: a range is
 * inclusive on both ends ({ start: Nov 2, end: Nov 8 } covers seven whole days).
 */

export type WeekStart = "MONDAY" | "SUNDAY";

export interface DateRange {
  start: Date;
  end: Date;
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Clamps the day so Jan 31 + 1 month is Feb 28/29, not Mar 3. */
export function addMonths(d: Date, n: number): Date {
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(d.getDate(), last));
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function startOfWeek(d: Date, weekStart: WeekStart): Date {
  const offset = weekStart === "MONDAY" ? (d.getDay() + 6) % 7 : d.getDay();
  return addDays(startOfDay(d), -offset);
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function dayDiff(a: Date, b: Date): number {
  // Rounding absorbs the 23h/25h days around DST changes.
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000);
}

export const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "thisWeek", label: "This week" },
  { key: "lastWeek", label: "Last week" },
  { key: "pastTwoWeeks", label: "Past two weeks" },
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "thisYear", label: "This year" },
  { key: "lastYear", label: "Last year" },
] as const;

export type DatePreset = (typeof DATE_PRESETS)[number]["key"];

export function presetRange(preset: DatePreset, today: Date, weekStart: WeekStart): DateRange {
  const t = startOfDay(today);
  const week = startOfWeek(t, weekStart);
  switch (preset) {
    case "today":
      return { start: t, end: t };
    case "yesterday": {
      const y = addDays(t, -1);
      return { start: y, end: y };
    }
    case "thisWeek":
      return { start: week, end: addDays(week, 6) };
    case "lastWeek":
      return { start: addDays(week, -7), end: addDays(week, -1) };
    case "pastTwoWeeks":
      // Clockify's "Past two weeks" is last week plus the current week.
      return { start: addDays(week, -7), end: addDays(week, 6) };
    case "thisMonth":
      return { start: startOfMonth(t), end: endOfMonth(t) };
    case "lastMonth": {
      const m = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      return { start: m, end: endOfMonth(m) };
    }
    case "thisYear":
      return { start: new Date(t.getFullYear(), 0, 1), end: new Date(t.getFullYear(), 11, 31) };
    case "lastYear":
      return { start: new Date(t.getFullYear() - 1, 0, 1), end: new Date(t.getFullYear() - 1, 11, 31) };
  }
}

export function matchPreset(range: DateRange, today: Date, weekStart: WeekStart): DatePreset | null {
  for (const p of DATE_PRESETS) {
    const r = presetRange(p.key, today, weekStart);
    if (sameDay(r.start, range.start) && sameDay(r.end, range.end)) return p.key;
  }
  return null;
}

/**
 * The report header arrows: whole-month (and whole-year) ranges step by months so
 * "This month" goes to the previous calendar month; anything else steps by its day count.
 */
export function shiftRange(range: DateRange, direction: 1 | -1): DateRange {
  const { start, end } = range;
  const wholeMonths = start.getDate() === 1 && sameDay(end, endOfMonth(end));
  if (wholeMonths) {
    const months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth() + 1;
    const s = new Date(start.getFullYear(), start.getMonth() + direction * months, 1);
    return { start: s, end: endOfMonth(new Date(s.getFullYear(), s.getMonth() + months - 1, 1)) };
  }
  const len = dayDiff(start, end) + 1;
  return { start: addDays(start, direction * len), end: addDays(end, direction * len) };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Clockify's default MM/DD/YYYY; callers with a user dateFormat pass their own formatter. */
export function formatDateDefault(d: Date): string {
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

export function monthGrid(month: Date, weekStart: WeekStart): Date[] {
  const first = startOfWeek(startOfMonth(month), weekStart);
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

export const WEEKDAY_LABELS_MONDAY = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;
export const WEEKDAY_LABELS_SUNDAY = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;
export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
